"use client";

import React from "react";
import { Truck, Search, Package, ShieldCheck, TrendingUp, CircleCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Custom RTD Formulation",
  icon: Search,
  bullets: ["Precision wellness profiles", "Probiotic & adaptogen fizzes", "Electrolyte recovery blends", "Flavor stability R&D"]
}, {
  title: "Counter-Pressure Canning",
  icon: Package,
  bullets: ["Sleek 12oz & 16oz formats", "High-speed carbonation", "Nitro-infusion technology", "Tunnel pasteurization"]
}, {
  title: "FDA & Compliance Support",
  icon: ShieldCheck,
  bullets: ["FDA-registered facility", "GMP certified standards", "Nutritional label review", "Batch-specific COA testing"]
}, {
  title: "Supply Chain Solutions",
  icon: Truck,
  bullets: ["Low-MOQ entry programs", "Premium ingredient sourcing", "Cold-chain logistics", "Inventory warehousing"]
}, {
  title: "Scalable Manufacturing",
  icon: TrendingUp,
  bullets: ["Pilot-batch development", "High-volume production", "Efficiency optimization", "Dedicated account team"]
}];
function ServiceCard({
  service,
  index
}: {
  service: typeof services[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div data-section-id="121974" ref={ref} data-index={index} className="h-full">
      <Card className={`h-full border border-white/10 hover:border-primary/50 transition-all duration-500 shadow-xl hover:shadow-primary/10 rounded-xl bg-card/40 backdrop-blur-xl flex flex-col transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
      transitionDelay: `${index * 100}ms`
    }}>
        <CardHeader className="pb-4 flex-none">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 shadow-[0_0_15px_rgba(20,255,150,0.2)]">
            <service.icon className="w-6 h-6 text-primary drop-shadow-[0_2px_2px_rgba(0,0,0,0.3)]" />
          </div>
          <CardTitle>
            <h3 className="text-lg font-sans font-semibold text-foreground tracking-tight leading-tight">
              {service.title}
            </h3>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-grow">
          <ul className="space-y-3">
            {service.bullets.map((bullet, j) => <li key={j} data-index={j} className="flex items-start gap-2.5 text-sm text-muted-foreground font-mono">
                <CircleCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <span className="leading-snug">{bullet}</span>
              </li>)}
          </ul>
        </CardContent>
      </Card>
    </div>;
}
export default function OurStrategicDistributionServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="our-core-capabilities" className="relative bg-background py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(hsl(var(--primary)/0.05)_1px,transparent_1px)] [background-size:24px_24px] opacity-60 pointer-events-none"></div>
      
      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-sans font-bold text-foreground mb-4 tracking-tighter">
            Our Core Capabilities
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Precision contract manufacturing for functional beverages, specializing in custom formulation and high-performance canning for the wellness industry.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-12 items-stretch">
          {services.map((service, i) => <ServiceCard key={i} service={service} index={i} />)}
        </div>
      </div>
    </section>;
}